import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent, type MouseEvent as ReactMouseEvent } from 'react'
import { useNavigate } from 'react-router-dom'

import { isStickerOwned, removeSticker, requestRestore, updateSticker, useStickers, type GenerationSticker } from '../api/generationStickers'
import { isAuthenticated } from '../api/auth'
import { getTaskStatus, isTaskGone, TASK_GONE_MESSAGE } from '../api/images'
import { pollDelayMs } from '../pages/generate/utils/constants'
import { markSeen, useActivityItems, type ActivityItem, type ActivityStatus } from '../api/assistantActivity'
import { clampLeft, clampTop, DRAG_THRESHOLD, readPos, settle, writePos, type Bounds, type StickersPos } from './stickersPosition'
import { RETRY_NOTE, RETRY_TITLE, taskTick } from '../api/taskTicks'
import { useTaskTicks } from '../hooks/useTaskTicks'

/**
 * Стопка стикеров незакрытых генераций (по умолчанию — правый верхний угол,
 * под шапкой).
 *
 * Заменяет прежнюю `GenerationPill`: та висела в шапке, показывала имя модели,
 * на завершении гасла через 5 секунд и вела в /files — то есть не возвращала
 * человека туда, откуда он начал. Стикер показывает карточку и результат,
 * держится до клика или крестика и возвращает на страницу старта.
 *
 * Два источника, одна стопка (22.09): медиа-генерации виджета (снимок в
 * sessionStorage — их ведёт браузер) и запуски умных помощников (список с
 * сервера — их ведёт бэк, см. assistantActivity.ts). Внешне не различаются:
 * человеку важно «что считается и куда вернуться», а не чей это движок.
 *
 * Стопку можно передвинуть, чтобы не мешала (владелец, 22.09): тянут за любой
 * стикер, перетаскивание начинается после сдвига в DRAG_THRESHOLD — короткое
 * нажатие остаётся открытием. После отпускания стопка прилипает к ближайшему
 * краю и держит вертикаль; положение помнится на устройстве (stickersPosition).
 * Ручка над стопкой — подсказка на наведении, двойной клик по ней возвращает
 * стопку на место.
 */
export default function GenerationStickers() {
	const stickers = useStickers()
	const activity = useActivityItems()
	const navigate = useNavigate()
	// Провайдер не пропустил звук готового ролика — сервер пересоздаёт его (28.09): подсказка стикера говорит почему дольше.
	useTaskTicks(stickers.some((s) => s.status === 'running'))

	// Стикеры, чью генерацию не ведёт живой запуск виджета (вкладку перезагрузили), доводит опрос сервера здесь:
	// генерацию закрывает ответ сервера, а не часы (план «Ждём ответа нейросети», Ф2). Раньше такой стикер висел
	// «в работе», пока его не закроют руками. Чем дольше идёт генерация, тем реже спрашиваем (pollDelayMs).
	const [pollRound, setPollRound] = useState(0)
	useEffect(() => {
		const orphans = stickers.filter((s) => s.status === 'running' && !!s.requestId && !isStickerOwned(s.id))
		if (orphans.length === 0) return
		let stop = false
		const now = Date.now()
		const wait = Math.min(...orphans.map((s) => pollDelayMs(now - s.startedAt)))
		const t = window.setTimeout(async () => {
			for (const s of orphans) {
				if (stop) return
				if (!isAuthenticated()) break // без входа сервер не спрашиваем (401 уводит на /login) — ждём входа
				try {
					const st = await getTaskStatus(s.requestId as string)
					if (stop) return
					if (st.status === 'completed' && st.result_url) {
						updateSticker(s.id, {
							status: 'done',
							resultUrl: st.image_urls?.[0] ?? st.result_url,
							resultUrls: st.result_urls ?? null,
						})
					} else if (st.status === 'failed') {
						updateSticker(s.id, { status: 'error', error: st.error_message_ru || st.error_message || undefined })
					}
				} catch (e) {
					if (isTaskGone(e)) updateSticker(s.id, { status: 'error', error: TASK_GONE_MESSAGE })
					/* иначе сеть моргнула — спросим на следующем круге */
				}
			}
			if (!stop) setPollRound((n) => n + 1)
		}, wait)
		return () => {
			stop = true
			window.clearTimeout(t)
		}
	}, [stickers, pollRound])

	const stackRef = useRef<HTMLDivElement>(null)
	const [pos, setPos] = useState<StickersPos | null>(() => readPos())
	/** Свободное положение во время перетаскивания; вне его — null. */
	const [dragXY, setDragXY] = useState<{ left: number; top: number } | null>(null)
	const drag = useRef<{ id: number; startX: number; startY: number; originLeft: number; originTop: number; moved: boolean } | null>(null)
	/** Последняя точка перетаскивания — для «куда лечь»: DOM к отпусканию мог не перерисоваться. */
	const dragPos = useRef<{ left: number; top: number } | null>(null)
	const detach = useRef<(() => void) | null>(null)
	const suppressClick = useRef(false)
	useEffect(() => () => detach.current?.(), [])

	const bounds = useCallback((): Bounds & { rect: DOMRect } => {
		const el = stackRef.current
		const rect = el?.getBoundingClientRect() ?? new DOMRect(0, 0, 0, 0)
		const cs = el ? getComputedStyle(el) : null
		return {
			vw: window.innerWidth,
			vh: window.innerHeight,
			width: rect.width,
			height: rect.height,
			inset: parseFloat(cs?.getPropertyValue('--gs-inset') ?? '') || 18,
			minTop: parseFloat(cs?.getPropertyValue('--gs-min-top') ?? '') || 72,
			rect,
		}
	}, [])

	// Стопка не должна уезжать за экран: при ресайзе, повороте телефона и когда
	// прибавился стикер (стопка стала выше) — прижимаем сохранённый верх к границам.
	// Текущее положение — через ref: обработчик ресайза живёт дольше одного рендера,
	// и со старым замыканием прижимал бы к границам уже отменённое значение.
	// Ресайз окна считаем на следующем кадре (размеры к событию ещё не устоялись —
	// на телефоне стопка уезжала за низ на 65 px), а смену высоты самой стопки
	// (мельче стикеры на узком экране, прибавился стикер) ловит ResizeObserver.
	const posRef = useRef(pos)
	useLayoutEffect(() => {
		posRef.current = pos
	}, [pos])
	const mounted = stickers.length + activity.length > 0
	useLayoutEffect(() => {
		const fit = () => {
			const p = posRef.current
			if (!p || drag.current) return
			const top = clampTop(p.top, bounds())
			if (top !== p.top) {
				const next = { ...p, top }
				posRef.current = next
				setPos(next)
				writePos(next)
			}
		}
		const onResize = () => window.requestAnimationFrame(fit)
		fit()
		window.addEventListener('resize', onResize)
		const el = stackRef.current
		const ro = el && typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => fit()) : null
		if (el) ro?.observe(el)
		return () => {
			window.removeEventListener('resize', onResize)
			ro?.disconnect()
		}
	}, [mounted, bounds])

	// Без setPointerCapture: захват перенацеливает click на захвативший элемент,
	// и кнопка стикера его не получала — короткое нажатие переставало открывать
	// (лог событий 22.09). Движение и отпускание слушаем на document, пока тянут.
	const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
		if (e.pointerType === 'mouse' && e.button !== 0) return
		const rect = stackRef.current?.getBoundingClientRect()
		if (!rect) return
		detach.current?.()
		const d = { id: e.pointerId, startX: e.clientX, startY: e.clientY, originLeft: rect.left, originTop: rect.top, moved: false }
		drag.current = d
		dragPos.current = null

		const onMove = (ev: PointerEvent) => {
			if (ev.pointerId !== d.id) return
			const dx = ev.clientX - d.startX
			const dy = ev.clientY - d.startY
			if (!d.moved) {
				if (Math.hypot(dx, dy) < DRAG_THRESHOLD) return
				d.moved = true
			}
			const b = bounds()
			const xy = { left: clampLeft(d.originLeft + dx, b), top: clampTop(d.originTop + dy, b) }
			dragPos.current = xy
			setDragXY(xy)
		}
		const onEnd = (ev: PointerEvent) => {
			if (ev.pointerId !== d.id) return
			detach.current?.()
			drag.current = null
			if (!d.moved) return
			const b = bounds()
			const xy = dragPos.current ?? { left: b.rect.left, top: b.rect.top }
			const next = settle({ left: xy.left, top: xy.top, width: b.width }, b)
			setPos(next)
			writePos(next)
			setDragXY(null)
			// Клик приходит следом за отпусканием — после перетаскивания это не выбор.
			suppressClick.current = true
			window.setTimeout(() => {
				suppressClick.current = false
			}, 0)
		}
		document.addEventListener('pointermove', onMove)
		document.addEventListener('pointerup', onEnd)
		document.addEventListener('pointercancel', onEnd)
		detach.current = () => {
			document.removeEventListener('pointermove', onMove)
			document.removeEventListener('pointerup', onEnd)
			document.removeEventListener('pointercancel', onEnd)
			detach.current = null
		}
	}

	const onClickCapture = (e: ReactMouseEvent<HTMLDivElement>) => {
		if (!suppressClick.current) return
		e.stopPropagation()
		e.preventDefault()
	}

	const resetPos = () => {
		setPos(null)
		writePos(null)
	}

	if (stickers.length === 0 && activity.length === 0) return null

	const open = (s: GenerationSticker) => {
		// Заявку оставляем всегда: карточка при открытии применяется заново и
		// штатно чистит и слот, и зону результата. Человеку нужно вернуть его
		// фото — и у готовой генерации, и у идущей (там результата ещё нет,
		// но фото в слоте показывает, ЧТО именно сейчас считается).
		requestRestore({
			cardId: s.origin.cardId,
			resultUrl: s.status === 'done' ? s.resultUrl : undefined,
			resultUrls: s.status === 'done' ? s.resultUrls : undefined,
			requestId: s.status === 'done' ? s.requestId : undefined,
			sourceImageDbKey: s.sourceImageDbKey,
			inputs: s.inputs,
		})
		// Снимаем только досмотренное. У идущей генерации стикер — единственный
		// индикатор: сняв его на клике, мы бы отняли у человека возможность
		// уйти со страницы ещё раз и вернуться (владелец, 21.09).
		if (s.status !== 'running') removeSticker(s.id)
		navigate(s.origin.path)
	}

	// Помощник: заявка не нужна — запуск на сервере, адрес открывает его как есть.
	const openActivity = (it: ActivityItem) => {
		if (it.status !== 'running') markSeen(it.id)
		navigate(it.path)
	}

	// Во время перетаскивания — свободная точка; в покое — сохранённый край,
	// либо ничего (положение по умолчанию задаёт CSS).
	const style: CSSProperties | undefined = dragXY
		? { left: dragXY.left, top: dragXY.top, right: 'auto' }
		: pos
			? { top: pos.top, left: pos.side === 'left' ? 'var(--gs-inset)' : 'auto', right: pos.side === 'right' ? 'var(--gs-inset)' : 'auto' }
			: undefined

	return (
		<div
			ref={stackRef}
			className={`gen-stickers${dragXY ? ' is-dragging' : ''}`}
			style={style}
			aria-label="Незакрытые генерации"
			onPointerDown={onPointerDown}
			onClickCapture={onClickCapture}
		>
			<div
				className="gen-stickers__grip"
				title="Потяните, чтобы передвинуть · двойной клик — вернуть на место"
				aria-hidden="true"
				onDoubleClick={resetPos}
			/>
			{stickers.map((s) => (
				<Sticker
					key={s.id}
					status={s.status}
					label={s.origin.cardTitle || s.modelName}
					note={s.status === 'running' && taskTick(s.requestId)?.retrying ? `${RETRY_TITLE} — ${RETRY_NOTE}` : null}
					image={s.resultUrl || s.origin.cardPreviewUrl}
					error={s.error}
					onOpen={() => open(s)}
					onClose={() => removeSticker(s.id)}
				/>
			))}
			{activity.map((it) => (
				<Sticker
					key={it.id}
					status={it.status}
					label={it.title}
					image={it.iconUrl ?? undefined}
					error={it.error}
					onOpen={() => openActivity(it)}
					onClose={() => markSeen(it.id)}
				/>
			))}
		</div>
	)
}

function Sticker({ status, label, note, image, error, onOpen, onClose }: {
	status: ActivityStatus
	label: string
	/** Пояснение к идущей генерации (ролик пересоздаётся) — в подсказку вместо подписи. */
	note?: string | null
	image?: string | null
	error?: string | null
	onOpen: () => void
	onClose: () => void
}) {
	return (
		<div className="gen-sticker">
			<button
				type="button"
				className={`gen-sticker__card gen-sticker__card--${status}`}
				onClick={onOpen}
				title={status === 'error' ? error || 'Ошибка генерации' : note ? `${label} · ${note}` : label}
				aria-label={`${label} — вернуться к генерации`}
			>
				{image ? (
					// draggable=false: иначе браузер начинал бы «перенос картинки»
					// раньше, чем стопка поймёт, что её тянут.
					<img className="gen-sticker__img" src={image} alt="" draggable={false} />
				) : (
					<span className="gen-sticker__img gen-sticker__img--empty" />
				)}
				{status === 'running' && (
					<>
						<span className="gen-sticker__scrim" />
						<span className="gen-sticker__spin" />
					</>
				)}
				{status === 'done' && (
					<span className="gen-sticker__badge gen-sticker__badge--done">
						<svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4">
							<polyline points="20,6 9,17 4,12" />
						</svg>
					</span>
				)}
				{status === 'error' && (
					<span className="gen-sticker__badge gen-sticker__badge--error">
						<svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4">
							<line x1="18" y1="6" x2="6" y2="18" />
							<line x1="6" y1="6" x2="18" y2="18" />
						</svg>
					</span>
				)}
				{/* Подпись внутри карточки, поверх затемнения: стикер —
				    одна плашка, а не картинка с отдельной плашкой текста. */}
				<span className="gen-sticker__veil" />
				<span className="gen-sticker__cap">{label}</span>
			</button>
			{/* Крестик — отдельной кнопкой, а не внутри карточки: вложенная
			    кнопка невалидна и ломает клавиатурную навигацию. */}
			<button type="button" className="gen-sticker__x" onClick={onClose} aria-label={`Убрать «${label}»`}>
				<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
					<line x1="18" y1="6" x2="6" y2="18" />
					<line x1="6" y1="6" x2="18" y2="18" />
				</svg>
			</button>
		</div>
	)
}
